/**
 * Build a multipart/form-data FormData from a plain object plus any
 * number of file blobs. Used by the admin editors when the editor
 * has a freshly-picked image to upload alongside the rest of the
 * form fields.
 *
 * Non-file values are appended as strings (zod parses them server-
 * side via the `coerceFormBody` middleware). File values are appended
 * with the field name as the FormData key, so the route's multer
 * middleware (`singleImage(fieldName)` / `productImages()`) picks
 * them up.
 *
 * A file value may be a single Blob/File (most common) or an array
 * of Blobs/Files (used for the product gallery, where up to 4 files
 * share the `gallery_file` field name). FormData supports repeated
 * keys, and multer's `fields()` reads them as an array.
 *
 * Arrays/objects are JSON.stringified so the server's zod schema
 * still gets parseable values.
 */
export type FormDataFileFields = Record<
  string,
  Blob | File | Blob[] | File[] | null | undefined
>

export function toFormData(
  fields: Record<string, unknown>,
  files: FormDataFileFields = {},
): FormData {
  const fd = new FormData()
  for (const [k, v] of Object.entries(fields)) {
    if (v == null) continue
    if (Array.isArray(v) || typeof v === 'object') {
      fd.append(k, JSON.stringify(v))
    } else if (typeof v === 'boolean') {
      fd.append(k, v ? 'true' : 'false')
    } else {
      fd.append(k, String(v))
    }
  }
  for (const [k, v] of Object.entries(files)) {
    if (v == null) continue
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item) fd.append(k, item)
      }
    } else {
      fd.append(k, v)
    }
  }
  return fd
}